import React from 'react';
import clsx from 'clsx';
import Image from 'next/image';

import { useRecursiveTimeout } from '@edx/hooks/use-recursive-timeout';
import { useSlider } from '@edx/hooks/use-slider';
import { TestimonialProps } from '@edx/components/testimonials-carousel';
import { Body } from '@edx/components/typography';

enum QuoteSize {
  small = 'text-sm leading-tight',
  medium = 'text-base leading-tight',
  large = 'text-lg leading-tight',
  xlarge = 'text-xl leading-snug',
}

enum Alignment {
  left = 'md:items-start md:text-left',
  center = '',
  right = 'md:items-end md:text-right',
}

type CarouselProps = {
  testimonials: TestimonialProps[];
  quoteSize?: keyof typeof QuoteSize;
  alignment?: keyof typeof Alignment;
};

type Props = {
  sectionClass?: string;
  containerClass?: string;
  hideAvatar?: boolean;
} & CarouselProps;

export const TestimonialsCarousel: React.FC<Props> = ({
  sectionClass,
  containerClass,
  testimonials,
  alignment = 'center',
  quoteSize = 'xlarge',
  hideAvatar,
}) => {
  const { ref, embla, selectedIndex, scrollSnaps, handleScrollTo } = useSlider({ loop: true });

  const autoplay = React.useCallback(() => {
    if (!embla) return;

    if (embla.canScrollNext()) {
      embla.scrollNext();
    } else {
      embla.scrollTo(0);
    }
  }, [embla]);

  const { play } = useRecursiveTimeout(autoplay, 5000);

  React.useEffect(() => {
    play();
  }, [play]);

  return (
    <section className={sectionClass}>
      <div className="w-full overflow-hidden" ref={ref}>
        <div className={clsx('flex space-x-4', containerClass)}>
          {testimonials.map(({ quote, avatar, name, title }) => (
            <div
              className={clsx(
                'relative min-w-full flex flex-col text-gray-300 font-body items-center text-center',
                Alignment[alignment]
              )}
              key={name}
            >
              <Body size="xlarge" className={clsx(QuoteSize[quoteSize])}>
                &ldquo;{quote}&rdquo;
              </Body>

              <div className="flex flex-col md:flex-row items-center mt-6 space-x-0 md:space-x-4">
                {!hideAvatar && (
                  <div className="w-20 flex-shrink-0">
                    <Image src={avatar} alt={name} width={80} height={80} className="rounded-full" />
                  </div>
                )}

                <div className="flex flex-col">
                  <Body tag="span" size="large">
                    {name}
                  </Body>
                  {title && (
                    <Body tag="span" className="text-gray-500 leading-tight">
                      {title}
                    </Body>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex list-none justify-center space-x-2">
        {scrollSnaps.map((_, index: number) => (
          <button
            key={index}
            className="relative w-3 h-3 cursor-pointer bg-transparent p-0 outline-none border-0 flex items-center focus:outline-none"
            type="button"
            onClick={(): void => handleScrollTo(index)}
          >
            <span className={clsx('h-2 w-2 rounded-full', index === selectedIndex ? 'bg-white' : 'bg-gray-600')} />
          </button>
        ))}
      </div>
    </section>
  );
};
